/**
 * Bounded retry for API calls that fail in front of the API (Cloud Run,
 * Firebase Hosting) rather than in it.
 *
 * Who answered is read from our own header, never from the content type: the
 * API's global limiter (express-rate-limit, legacy headers on) stamps
 * `X-RateLimit-Limit` on every response that passed through it, and the
 * route limiters stamp `RateLimit-Limit`. A response with neither never
 * reached the app. The API's own unhandled 500s are Express's HTML page, so
 * "not JSON" says nothing about who answered.
 *
 *   - cloudRun429:  a 429 the app did not send — Cloud Run's "no available
 *                   instance". No container saw the request, so it is re-sent
 *                   for every method: 2 retries, 400 ms then 1200 ms, ±30%.
 *   - upstream5xx:  a 500 or 503 the app did not send — Firebase Hosting's
 *                   plain-text "Internal Error", or Cloud Run's own 503. The
 *                   backend may have run, so GET/HEAD only, same backoff. The
 *                   retries after one ask the API to reload this user's login.
 *   - limiter429:   the app's limiter: a 429 from the app, not JSON, with a
 *                   Retry-After. Waited out once, GET/HEAD only, when
 *                   Retry-After is 3 s or less.
 *   - app429:       any other 429 from the app (a route's JSON 429). Never.
 *
 * Anything else the app answered is never retried here.
 *
 * Cross-origin callers (VITE_BACKEND_URL pointing straight at the API, as in
 * local dev) only see the headers the API's CORS policy exposes; until it
 * exposes X-RateLimit-Limit / RateLimit-Limit / Retry-After, every 429 there
 * reads as cloudRun429 and every 500/503 as upstream5xx. Production calls the
 * same-origin /api proxy and sees every header.
 *
 * Framework-free on purpose, like serviceStatus.js: api.js cannot be imported
 * under jest (it reads `import.meta`), so the loop — and `fetchApi`, the fetch
 * every api.js call goes through — lives here, and the tests drive it with a
 * mocked fetch and fake timers. The proxy function keeps its own copy of the
 * classification (dashboard/functions/retryPolicy.js): keep the two in step.
 */

import { isJsonContentType } from './apiResponse';
import { isServiceFailure, isTransportFailure, reportServiceFailure } from './serviceStatus';

export const FAILURE = Object.freeze({
  CLOUD_RUN_429: 'cloudRun429',
  UPSTREAM_5XX: 'upstream5xx',
  LIMITER_429: 'limiter429',
  APP_429: 'app429',
  NONE: 'none',
});

/** Failures the policy may re-send; a call that still ends in one gave up. */
const RETRYABLE = new Set([FAILURE.CLOUD_RUN_429, FAILURE.UPSTREAM_5XX, FAILURE.LIMITER_429]);

/** Wait before the first and the second retry; their count is the retry cap. */
export const BACKOFF_MS = Object.freeze([400, 1200]);

/** ±30% around each backoff step, so a burst of callers does not retry in step. */
export const JITTER = 0.3;

/** The longest Retry-After a limiter429 is waited out for. */
export const MAX_LIMITER_WAIT_MS = 3_000;

/** Sent on every retry, so the API's logs can tell retries apart. */
export const RETRY_HEADER = 'X-QC-Retry';

/** Sent on retries after an upstream5xx: the API drops this user's cached login. */
export const REFRESH_AUTH_HEADER = 'X-QC-Refresh-Auth';

/** Window event fired after a call recovered from an upstream5xx (App.js listens). */
export const EDGE_RETRY_EVENT = 'qc:edge-retry';

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD']);

const UPSTREAM_STATUSES = new Set([500, 503]);

// Responses that came back through at least one retry, and what each retry was for.
const retryHistories = new WeakMap();

/** One header from fetch Headers or an axios-style plain object. */
const readHeader = (headers, name) => {
  if (!headers) return null;
  if (typeof headers.get === 'function') return headers.get(name);
  const key = Object.keys(headers).find((candidate) => candidate.toLowerCase() === name.toLowerCase());
  return key === undefined ? null : headers[key];
};

const hasHeader = (headers, name) => readHeader(headers, name) != null;

/** Did the app answer? Its limiters stamp every response that reached it. */
export const isFromApp = (response) =>
  hasHeader(response?.headers, 'X-RateLimit-Limit') || hasHeader(response?.headers, 'RateLimit-Limit');

/**
 * What kind of failure a response is, for the retry policy. Anything that is
 * not a response (a thrown error, null) is FAILURE.NONE: transport failures
 * are not retried here.
 */
export const classifyFailure = (response) => {
  if (!response || typeof response.status !== 'number') return FAILURE.NONE;
  const fromApp = isFromApp(response);
  if (response.status === 429) {
    if (!fromApp) return FAILURE.CLOUD_RUN_429;
    const json = isJsonContentType(readHeader(response.headers, 'Content-Type'));
    if (!json && hasHeader(response.headers, 'Retry-After')) return FAILURE.LIMITER_429;
    return FAILURE.APP_429;
  }
  if (UPSTREAM_STATUSES.has(response.status) && !fromApp) return FAILURE.UPSTREAM_5XX;
  return FAILURE.NONE;
};

/** Retry-After as milliseconds (delta-seconds or an HTTP date), or null. */
export const parseRetryAfter = (value, now = Date.now()) => {
  if (value == null || value === '') return null;
  const text = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(text)) return Math.round(Number(text) * 1000);
  const date = Date.parse(text);
  if (Number.isNaN(date)) return null;
  return Math.max(0, date - now);
};

const withJitter = (ms, random) => Math.round(ms * (1 + JITTER * (2 * random() - 1)));

const NO_RETRY = Object.freeze({ retry: false, delayMs: 0 });

/**
 * Whether to re-send a call that came back as `failure`, and after how long.
 *
 * @param {object} options
 * @param {string} options.failure       a FAILURE value (from classifyFailure)
 * @param {string} [options.method]      the request's method (fetch's default is GET)
 * @param {string[]} [options.history]   the failures already retried for this call
 * @param {number|null} [options.retryAfterMs] the response's Retry-After, parsed
 * @param {() => number} [options.random] Math.random, injectable for tests
 * @returns {{ retry: boolean, delayMs: number }}
 */
export const retryPolicy = ({ failure, method = 'GET', history = [], retryAfterMs = null, random = Math.random }) => {
  if (history.length >= BACKOFF_MS.length) return NO_RETRY;
  const idempotent = IDEMPOTENT_METHODS.has(String(method || 'GET').toUpperCase());
  switch (failure) {
    case FAILURE.CLOUD_RUN_429:
      return { retry: true, delayMs: withJitter(BACKOFF_MS[history.length], random) };
    case FAILURE.UPSTREAM_5XX:
      if (!idempotent) return NO_RETRY;
      return { retry: true, delayMs: withJitter(BACKOFF_MS[history.length], random) };
    case FAILURE.LIMITER_429:
      if (!idempotent || history.includes(FAILURE.LIMITER_429)) return NO_RETRY;
      if (retryAfterMs == null || retryAfterMs > MAX_LIMITER_WAIT_MS) return NO_RETRY;
      return { retry: true, delayMs: retryAfterMs };
    default:
      return NO_RETRY;
  }
};

const toHeaderObject = (headers) => {
  if (!headers) return {};
  if (Array.isArray(headers)) return Object.fromEntries(headers);
  if (typeof headers.forEach === 'function') {
    const plain = {};
    headers.forEach((value, name) => { plain[name] = value; });
    return plain;
  }
  return { ...headers };
};

/**
 * The init for a retry: `cache: 'reload'` goes past the browser's HTTP cache
 * (no If-None-Match / 304 path) and replaces the copy it holds; the retry is
 * marked; and once an upstream5xx has been seen for the call, the API is asked
 * to reload this user's login.
 */
export const retryInit = (init = {}, history = []) => {
  const headers = { ...toHeaderObject(init.headers), [RETRY_HEADER]: '1' };
  if (history.includes(FAILURE.UPSTREAM_5XX)) headers[REFRESH_AUTH_HEADER] = '1';
  return { ...init, cache: 'reload', headers };
};

/** The failures a response was retried for; empty when it was not retried. */
export const getRetryHistory = (response) =>
  (response && typeof response === 'object' && retryHistories.get(response)) || [];

/**
 * Did the retry layer give up on this response: it was retried, and the last
 * answer is still one it retries? Errors built from it carry
 * `retriesExhausted`, so React Query does not retry them again (queryRetry.js).
 */
export const retriesExhaustedFor = (response) =>
  getRetryHistory(response).length > 0 && RETRYABLE.has(classifyFailure(response));

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Free the connection behind a response we are about to throw away.
const discardBody = async (response) => {
  try {
    await response.body?.cancel?.();
  } catch {
    // Already consumed, locked, or no body: nothing to release.
  }
};

/**
 * fetch with the policy above. Resolves to the final response — after the last
 * allowed retry that is the last failure, body unread, for the caller to
 * handle as usual. A rejected fetch (transport failure) is not retried and
 * rejects as it would without this helper; one that rejects on a retry
 * carries `retriesExhausted`.
 *
 * @param {(url: string, init: object) => Promise<Response>} fetchImpl
 * @param {string} url
 * @param {object} [init]
 * @param {object} [options]
 * @param {(ms: number) => Promise<void>} [options.sleep]
 * @param {() => number} [options.random]
 * @param {(retry: { failure: string, attempt: number, delayMs: number, url: string }) => void} [options.onRetry]
 * @returns {Promise<Response>}
 */
export const fetchWithRetry = async (
  fetchImpl,
  url,
  init = {},
  { sleep = defaultSleep, random = Math.random, onRetry } = {},
) => {
  const history = [];
  let attemptInit = init;
  for (;;) {
    let response;
    try {
      response = await fetchImpl(url, attemptInit);
    } catch (error) {
      if (history.length && error && typeof error === 'object') error.retriesExhausted = true;
      throw error;
    }
    const failure = classifyFailure(response);
    const { retry, delayMs } = retryPolicy({
      failure,
      method: init.method,
      history,
      retryAfterMs: parseRetryAfter(readHeader(response.headers, 'Retry-After')),
      random,
    });
    if (!retry) {
      if (history.length && response && typeof response === 'object') retryHistories.set(response, [...history]);
      return response;
    }
    history.push(failure);
    onRetry?.({ failure, attempt: history.length, delayMs, url });
    await discardBody(response);
    await sleep(delayMs);
    attemptInit = retryInit(init, history);
  }
};

/**
 * Fired after a call that needed an upstream5xx retry got its answer from the
 * app: the path is healthy again, so App.js refetches the queries that failed
 * during the blip. Not when the retries ran out — refetching then would only
 * add load to a failing edge, and every refetch that failed again would fire
 * it again. The retried call itself needs nothing more: its retry already
 * went past the HTTP cache.
 */
export const announceEdgeRecovery = (url, response) => {
  if (typeof window === 'undefined') return;
  if (!getRetryHistory(response).includes(FAILURE.UPSTREAM_5XX)) return;
  if (!isFromApp(response)) return;
  window.dispatchEvent(new CustomEvent(EDGE_RETRY_EVENT, { detail: { url } }));
};

/**
 * The fetch behind every api.js call (its `apiFetch`): fetchWithRetry, unless
 * `retry: false` (uploads are never re-sent), then the service-down report.
 * Only the final answer reports, so a blip that a retry absorbs never starts
 * the store's confirmation streak; a rejected fetch reports at once, as before.
 *
 * @param {string} url
 * @param {object} [init]
 * @param {object} [options]
 * @param {boolean} [options.retry]  false for a single attempt
 * @param {Function} [options.fetchImpl] tests only; the global fetch otherwise
 * @param {(reason: 'network'|'gateway') => void} [options.report] tests only
 * @returns {Promise<Response>}
 */
export const fetchApi = async (url, init, {
  retry = true,
  fetchImpl = (target, options) => fetch(target, options),
  report = reportServiceFailure,
  ...retryOptions
} = {}) => {
  let response;
  try {
    response = retry
      ? await fetchWithRetry(fetchImpl, url, init, retryOptions)
      : await fetchImpl(url, init);
  } catch (error) {
    // A request its caller cancelled (a superseded search) says nothing about
    // the service.
    if (isTransportFailure(error) && !init?.signal?.aborted) report('network');
    throw error;
  }
  if (isServiceFailure(response)) report('gateway');
  announceEdgeRecovery(url, response);
  return response;
};
